import React, { useEffect, useRef, useState } from 'react'

import { Link} from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCircleInfo, faCopy, faEllipsisVertical, faFile, faHistory, faPen, faSearch, faTimes, faTrashRestore, faXmark } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

import { useProductForm } from 'hooks/useProductForm'
import { ProductForm } from 'components/forms/ProductForm'
import { useDesignDocumentsForm } from 'hooks/useDesignDocumentsForm'
import { usePipeOpenings } from 'hooks/usePipeOpenings'
import { usePipeMaterials } from 'hooks/usePipeMaterials'
import { useProductTypes } from 'hooks/useProductTypes'
import { User } from 'hooks/useUsers'
import { EDITS_REQUESTED, Metadata, APPROVED, PENDING_REVIEW, ProductSearchResult, ARCHIVED, ADD_NEW_PRODUCT_LINE, READY_FOR_APPROVAL } from 'models/SearchResult'
import { IProductSearchResponse } from 'models/SearchResponse'
import { DATE_TIME_FORMAT, ConfigValueItem, ProductTypeConfigValueItem } from 'constants/Constants'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { PipeOpeningConfig } from 'pages/settings/configuration/ManagePipeOpeningsScreen'
import { IndexProductDocument } from 'IndexProductScreen'
import { Permissions } from 'AppController'
import { isAnyAdmin, isMerchant, isSuperAdmin } from 'models/User'

import { SearchFilters, useSearchQueryAndFilters } from 'hooks/useSearchQueryAndFilters'
import { ProductFiltersMenu } from 'components/filters/ProductFiltersMenu'
import { ManageProductCounts } from 'hooks/useManagedProductCounts'
import { ConfirmActionModal } from 'components/modals/ConfirmActionModal'
import { useProductVersions } from 'hooks/useProductVersions'
import { StatusTag, VersionHistoryModal } from './VersionHistoryModal'
import { Comment } from './Comment'
import { FlowControlDeviceConfig, useFlowControlDevices } from 'hooks/useFlowControlDevices'
import { toastError, toastSuccess } from 'services/Toast'
import { RequestEditsFormModal } from './RequestEditsFormModal'
import { useRequestEditsFormModal } from './useRequestEditsFormModal'
import config from 'config'
import { isAddNewProductLine, isATypeOfChangeRequest, isATypeOfUpissue, isChangeRequestForProductRequiringUpissue, isEditProductLine, isUpissueProduct } from 'utils/ActiveAction'
import { isArchived, isEditsRequested, isPendingReview, isReadyForApproval } from 'utils/Metadata'
import { useAuth0Authentication } from 'hooks/useAuth0Authentication'

const ManageProductView = ({
  product,
  isMerchant,
  productTypes,
  pipeOpenings,
  pipeMaterials,
  flowControlDevices,
}: {
  product: ProductSearchResult,
  isMerchant: boolean,
  productTypes: ProductTypeConfigValueItem[],
  pipeOpenings: PipeOpeningConfig[],
  pipeMaterials: ConfigValueItem[],
  flowControlDevices: FlowControlDeviceConfig[]
}) => {

  const productForm = useProductForm() 
  const designDocumentsForm = useDesignDocumentsForm() 

  useEffect(() => {
    productForm.loadFormState(product)
    designDocumentsForm.loadFormState(product.designDocuments)
  }, [product])

  return (
      <ProductForm
        isMerchant={isMerchant}
        formView={'view'}
        productForm={productForm}
        designDocumentsForm={designDocumentsForm}
        productTypes={productTypes}
        pipeOpenings={pipeOpenings}
        pipeMaterials={pipeMaterials}
        flowControlDevices={flowControlDevices}
         />
  )
}

export enum ActiveTab {
  PENDING_REVIEW, 
  EDITS_REQUESTED,
  ARCHIVED
}

const Metadata = ({ productCode, metadata, loggedInUser, activeTab } : { productCode: string, metadata: Metadata, loggedInUser: User, activeTab: ActiveTab}) => {

  const toMoment = (timestamp: string): moment.Moment => moment(timestamp, DATE_TIME_FORMAT)

  const isLastEditedSameOrAfterLastModerated = metadata?.lastModerated && toMoment(metadata?.lastEdited.timestamp).isSameOrAfter(toMoment(metadata?.lastModerated?.timestamp))
  const isVersionAfterInitiatedOnVersion = metadata?.activeAction?.initiatedOnVersion === metadata.version - 1
  const isLastEditedSameOrAfterInitiated = toMoment(metadata.lastEdited.timestamp).isSameOrAfter(toMoment(metadata?.activeAction?.initiated?.timestamp))
  const isNewlyUpissuedProduct = isUpissueProduct(metadata?.activeAction) && isVersionAfterInitiatedOnVersion
  

  const shouldShowPendingReview = (): boolean => {

    const one: boolean = 
    isPendingReview(metadata) &&
    metadata?.version !== 1 &&
    metadata?.lastEdited !== undefined &&
    // Don't display for the first update of an upissue
    !isNewlyUpissuedProduct
  
  // if we have activeAction do this
  const two: boolean = metadata?.activeAction && 
    metadata?.activeAction?.initiatedOnVersion !== metadata.version && 
      (
        isLastEditedSameOrAfterInitiated &&
        metadata?.lastModerated && toMoment(metadata.lastEdited.timestamp).isSameOrBefore(toMoment(metadata?.lastModerated?.timestamp))
      )
      // handles when is new product with first edit
      || !metadata?.lastModerated
      // handles when is existing product with first edit
      || (isLastEditedSameOrAfterLastModerated && isVersionAfterInitiatedOnVersion)
  
  // and if we don't - do this
 const three: boolean =  !metadata?.activeAction && 
    (isLastEditedSameOrAfterLastModerated || !metadata?.lastModerated)

      return one && (two || three)
  }

  const renderPendingReview = () => {

    const isNewProductLineAndReadyForApproval = isReadyForApproval(metadata) && isAddNewProductLine(metadata?.activeAction)
    const isCreated = isPendingReview(metadata) && metadata?.version === 1 && metadata.lastEdited && !metadata.lastModerated

    return (
      <>
          {isNewlyUpissuedProduct && metadata.lastEdited
            ?
              <div className="columns">
                <div className="column">
                  <Comment 
                    messageStyle={`is-danger`}
                    commenterName={metadata.lastEdited.byFullName}
                    loggedInUser={loggedInUser}
                    actionText={`upissued a product for review`}
                    timestamp={metadata.lastEdited.timestamp}
                    comment={``} 
                    ComponentPulledRight={
                      <span className="tag is-danger is-small is-pulled-right">Upissued</span>
                    }
                    />
                </div>
              </div>
            :
          (isNewProductLineAndReadyForApproval && metadata.lastEdited && metadata.lastModerated)
            ?
              <div className="columns">
                <div className="column">
                  <Comment 
                    messageStyle={`is-primary`}
                    commenterName={metadata.lastModerated.byFullName}
                    loggedInUser={loggedInUser}
                    actionText={`marked this new product line as ready for approval`}
                    timestamp={metadata.lastModerated.timestamp}
                    comment={``} 
                    ComponentPulledRight={
                      <span className="tag is-primary is-small is-pulled-right">Ready for Approval</span>
                    }
                    />
                </div>
              </div>
            :
          isCreated
          ?
            <div className="columns">
              <div className="column">
                <Comment 
                  messageStyle={`is-success`}
                  commenterName={metadata.lastEdited.byFullName}
                  loggedInUser={loggedInUser}
                  actionText={metadata?.activeAction?.type === ADD_NEW_PRODUCT_LINE ? `added a new product line for review` : `added a new product for review`}
                  timestamp={metadata.lastEdited.timestamp}
                  comment={``} 
                  ComponentPulledRight={
                    <span className="tag is-success is-small is-pulled-right">Created</span>
                  }
                  />
              </div>
            </div>
          :
          null
        }
    
        {/* TODO -This is super confusing - make it better */}
        {/* has made edits only I.e pending review after first edit */}
        {shouldShowPendingReview()
          ?                         
          <div className="columns">
            <div className="column">
            <Comment 
              messageStyle={`is-info`}
              commenterName={metadata.lastEdited.byFullName}
              loggedInUser={loggedInUser}
              actionText={`has made edits`}
              timestamp={metadata.lastEdited.timestamp}
              comment={metadata.lastEdited.comment}
              ComponentPulledRight={
                <span className="tag is-link is-small is-pulled-right">Pending Review</span>
              }
              />
            </div>
          </div>
          : isPendingReview(metadata) &&
          metadata.lastEdited &&
          metadata.lastModerated &&
    
          // if we have activeAction do this
          ((metadata?.activeAction && 
            !isVersionAfterInitiatedOnVersion &&
            (isLastEditedSameOrAfterInitiated &&
            toMoment(metadata.lastModerated.timestamp).isSameOrAfter(toMoment(metadata?.activeAction?.initiated?.timestamp)))) || 
            // and if we don't - do this
            (!metadata?.activeAction && 
              (  toMoment(metadata.lastEdited.timestamp).isSameOrAfter(toMoment(metadata?.lastModerated?.timestamp)) )
            )
          )
            ?
            <>
              <div className="columns">
                <div className="column is-10">
                  <Comment 
                    messageStyle={`is-dark`}
                    commenterName={metadata.lastModerated.byFullName}
                    loggedInUser={loggedInUser}
                    actionText={`requested edits`}
                    timestamp={metadata.lastModerated.timestamp}
                    comment={metadata.lastModerated.comment} 
                    ComponentPulledRight={
                      <span className="tag is-warning is-small is-pulled-right">Edits Requested</span>
                    }
                    />
                </div>
              </div>
    
              {/* has made edits */}
              <div className="columns">
                <div className="column is-offset-2 is-10">
                <Comment 
                  messageStyle={`is-info`}
                  commenterName={metadata.lastEdited.byFullName}
                  loggedInUser={loggedInUser}
                  actionText={`has made edits`}
                  timestamp={metadata.lastEdited.timestamp}
                  comment={metadata.lastEdited.comment}
                  ComponentPulledRight={
                    <span className="tag is-link is-small is-pulled-right">Pending Review</span>
                  }
                  />
                </div>
              </div>
            </>
            :
            null
          }
          </>
        )
    }

  switch(activeTab) {

      case ActiveTab.EDITS_REQUESTED: return (
        isEditsRequested(metadata) && 
          <Comment 
            messageStyle={`is-dark`}
            commenterName={metadata.lastModerated.byFullName}
            loggedInUser={loggedInUser}
            actionText={`has requested edits`}
            timestamp={metadata.lastModerated.timestamp}
            comment={metadata.lastModerated.comment}
            ComponentPulledRight={
              // Don't allow edit if it's from same person who requested edits
              metadata.lastModerated && metadata.lastModerated.byUserId !== loggedInUser.user_id 
              ?
              <Link to={`/edit/product/${productCode}`} state={{from: metadata.status}}>
                <button className="button is-warning is-rounded is-pulled-right">Edit</button>
              </Link>
              :
              null
            } />
      )

      case ActiveTab.ARCHIVED: return (
        isArchived(metadata) && metadata.lastArchived &&
          <div className="columns">
            <div className="column">
              <Comment 
                messageStyle={`is-danger`}
                commenterName={metadata.lastArchived.byFullName}
                loggedInUser={loggedInUser}
                actionText={`archived this product`}
                timestamp={metadata.lastArchived.timestamp}
                comment={``} 
                ComponentPulledRight={
                  <span className="tag is-danger is-small is-pulled-right">Archived</span>
                }
                />
            </div>
          </div>
      )

      case ActiveTab.PENDING_REVIEW: return renderPendingReview()

    }

}

const ManageProductsScreen = ({ 
  loggedInUser, 
  activeTab,
  manageProductCounts, // TODO - should this be a Context?
  refreshCounts,
}:{ 
  loggedInUser: User, 
  activeTab: ActiveTab,
  manageProductCounts: ManageProductCounts,
  refreshCounts: () => void
}) => {

    const { getAuth0AccessToken } = useAuth0Authentication()

    const [didProductUpdate, setDidProductUpdate] = useState(false)
    const isFirstRenderActiveTab = useRef(true);

    const [isVersionHistoryModalActive, setIsVersionHistoryModalActive] = useState(false)
    const [isConfirmApprovedModalActive, setIsConfirmApprovedModalActive] = useState<boolean>(false)
    const [isMarkReadyForReviewModalActive, setIsMarkReadyForReviewModalActive] = useState<boolean>(false)

    const { pipeOpenings } = usePipeOpenings()
    const { pipeMaterials } = usePipeMaterials()
    const { productTypes } = useProductTypes()
    const { flowControlDevices } = useFlowControlDevices()
    const {productVersions, isLoading: isProductVersionsLoading, getProductVersions } = useProductVersions()
    const requestEditsFormModal = useRequestEditsFormModal(setDidProductUpdate)

    const [productSearchResponse, setProductSearchResponse] = useState<IProductSearchResponse>()

    const [isMarkAsApprovedLoading, setIsMarkAsApprovedLoading] = useState<boolean>(false)
    const [isMarkReadyForReviewLoading, setIsMarkReadyForReviewLoading] = useState<boolean>(false)
    const [isRestoreProductLoading, setIsRestoreProductLoading] = useState<boolean>(false)

    // TODO - can probably cache both of these.
    const [selectedProduct, setSelectedProduct] = useState<ProductSearchResult>(null)

    const isUpissuedProduct: boolean = isATypeOfUpissue(selectedProduct?.metadata?.activeAction)

    const { productFilters, searchQuery, isProductFiltersLoaded, isSearchQueryLoaded, onChangeSearchQuery, onClearFilters, onClearSearch, onClickSearch, onCheckFilter: onCheckProductFilter, onCheckNoneFilter } = useSearchQueryAndFilters('products')

    // TODO - make a hook - repeated in manageProducts
    const search = async (currentPage: number, searchQuery: string, filters?: SearchFilters) => {

      const accessToken: string = await getAuth0AccessToken()

      const pageParam: string = currentPage ? `&page=${currentPage}` : ``

      const pendingReviewFilter = `&metadata.status=${PENDING_REVIEW} OR ${READY_FOR_APPROVAL}`
      const editsRequestedFilter = `&metadata.status=${EDITS_REQUESTED}`
      const archivedFilter = `&metadata.status=${ARCHIVED}`

      const statusFilter: string = activeTab === ActiveTab.PENDING_REVIEW ? pendingReviewFilter : activeTab === ActiveTab.EDITS_REQUESTED ? editsRequestedFilter : activeTab === ActiveTab.ARCHIVED ? archivedFilter : ``

      if (filters) {
  
        const steelworkCodeFilters: string = filters && filters.steelworkCode && filters.steelworkCode.appliedFilters.length > 0  ? `&steelworkCode=${filters.steelworkCode.appliedFilters.join(',')}` : ''
        const handrailFilters: string = filters && filters.handrail && filters.handrail.appliedFilters.length > 0  ? `&handrail=${filters.handrail.appliedFilters.join(',')}` : ''
        const productTypeFilters: string = filters && filters.productType && filters.productType.appliedFilters.length > 0  ? `&productType=${filters.productType.appliedFilters.join(',')}` : ''
        const toeFilters: string = filters && filters.toe && filters.toe.appliedFilters.length > 0  ? `&toe=${filters.toe.appliedFilters.join(',')}` : ''
        const secondaryCastingFilters: string = filters && filters.secondaryCasting && filters.secondaryCasting.appliedFilters.length > 0  ? `&secondaryCasting=${filters.secondaryCasting.appliedFilters.join(',')}` : ''
        const reinforcingFilters: string = filters && filters.reinforcing && filters.reinforcing.appliedFilters.length > 0  ? `&reinforcing=${filters.reinforcing.appliedFilters.join(',')}` : ''
       
        const flowControlDeviceSizeFilters: string = filters && filters.flowControlDeviceSize && filters.flowControlDeviceSize.appliedFilters.length > 0  ? `&flowControlDevices.size=${filters.flowControlDeviceSize.appliedFilters.join(',')}` : ''
        const flowControlDeviceTypeFilters: string = filters && filters.flowControlDeviceType && filters.flowControlDeviceType.appliedFilters.length > 0  ? `&flowControlDevices.type=${filters.flowControlDeviceType.appliedFilters.join(',')}` : ''
  
        const invertLevelFilters: string = filters && filters.invertLevel && filters.invertLevel.appliedFilters.length > 0  ? `&pipeOpenings.invertLevel=${filters.invertLevel.appliedFilters.join(',')}` : ''
        const openingLevelFilters: string = filters && filters.openingLevel && filters.openingLevel.appliedFilters.length > 0  ? `&pipeOpenings.openingLevel=${filters.openingLevel.appliedFilters.join(',')}` : ''
        const openingSizeFilters: string = filters && filters.openingSize && filters.openingSize.appliedFilters.length > 0  ? `&pipeOpenings.openingSize=${filters.openingSize.appliedFilters.join(',')}` : ''
        const insideDiameterFilters: string = filters && filters.insideDiameter && filters.insideDiameter.appliedFilters.length > 0  ? `&pipeOpenings.insideDiameter=${filters.insideDiameter.appliedFilters.join(',')}` : ''
        const pipeMaterialFilters: string = filters && filters.pipeMaterial && filters.pipeMaterial.appliedFilters.length > 0  ? `&pipeOpenings.material=${filters.pipeMaterial.appliedFilters.join(',')}` : ''
        const statusFilters: string = filters && filters.status && filters.status.appliedFilters.length > 0  ? `&metadata.status=${filters.status.appliedFilters.join(',')}` : ''

        const hasHandrailFilter: string = filters?.handrail?.hasNoneSelected ? `&hasHandrail=false` : ``
        const hasSteelworkCodeFilter: string = filters?.steelworkCode?.hasNoneSelected ? `&hasSteelwork=false` : ``
        const hasToeFilter: string = filters?.toe?.hasNoneSelected ? `&hasToe=false` : ``
        const hasSecondaryCastingFilter: string = filters?.secondaryCasting?.hasNoneSelected ? `&hasSecondaryCasting=false` : ``
        const hasFlowControlDeviceFilter: string = (filters?.flowControlDeviceSize?.hasNoneSelected || filters?.flowControlDeviceType?.hasNoneSelected) ? `&hasFlowControlDevice=false` : ``
        const hasPipeOpeningFilter: string = (filters?.invertLevel?.hasNoneSelected || filters?.openingLevel?.hasNoneSelected || filters?.openingSize?.hasNoneSelected || filters?.insideDiameter?.hasNoneSelected || filters?.pipeMaterial?.hasNoneSelected) ? `&hasPipeOpenings=false` : ``
         
        const response = await fetch(
          `${config.apiUrl}/products/search?q=${searchQuery}${statusFilter}${pageParam}${steelworkCodeFilters}${handrailFilters}${productTypeFilters}${insideDiameterFilters}${pipeMaterialFilters}${toeFilters}${flowControlDeviceSizeFilters}${flowControlDeviceTypeFilters}${secondaryCastingFilters}${reinforcingFilters}${invertLevelFilters}${openingLevelFilters}${openingSizeFilters}${hasHandrailFilter}${hasSteelworkCodeFilter}${hasToeFilter}${hasSecondaryCastingFilter}${hasFlowControlDeviceFilter}${hasPipeOpeningFilter}${statusFilters}`
            , {
              method: 'GET',
              redirect: 'follow',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            }
            })
  
            const searchResponse: IProductSearchResponse = await response.json()
            setProductSearchResponse(searchResponse)     
            setSelectedProduct(searchResponse.results[0] || null)
            setDidProductUpdate(false)
        
      } else {

        const response = await fetch(
          `${config.apiUrl}/products/search?${statusFilter}${pageParam}`
            , {
              method: 'GET',
              redirect: 'follow',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            }
            })
  
            const searchResponse: IProductSearchResponse = await response.json()

            setProductSearchResponse(searchResponse)     
            setSelectedProduct(searchResponse.results[0] || null)
            setDidProductUpdate(false)
      }

    }
    
    useEffect(() => {

        if (didProductUpdate) {
        
        // delay fetching to allow time for indexing to happen on BE
        // TODO - handle properly with loading state?
        const timeoutId = setTimeout(() => {
            search(0, searchQuery, productFilters)
            .catch((error) => console.log(error))
    
            refreshCounts()
          
        }, 2500);

        // Cleanup function to clear the timeout if the component unmounts
        return () => clearTimeout(timeoutId);

      }
            
      }, [didProductUpdate])

      useEffect(() => {
          if (isSearchQueryLoaded && isProductFiltersLoaded) {
            search(0, searchQuery, productFilters)
            .catch((error) => console.log(error))
    
            refreshCounts()

          }

  }, [searchQuery, productFilters])

      useEffect(() => {   
        if (isFirstRenderActiveTab.current) {
          isFirstRenderActiveTab.current = false;
          return; // Skip the effect on first render
        }

        search(0, null) // when active tab changes we want no product filters to be applied.
        .catch((error) => console.log(error))
      }, [activeTab])


      const onClickMarkReadyForReview = (e: React.MouseEvent) => {
        e.preventDefault();

        setIsMarkReadyForReviewModalActive(true)
      }
      

      const onClickMarkAsApproved = (e: React.MouseEvent) => {
        e.preventDefault();

        setIsConfirmApprovedModalActive(true)
      }

      const markAsApproved = async () => {

          setIsMarkAsApprovedLoading(true)          

          const shouldUpissue: boolean = 
            isUpissuedProduct || 
            (selectedProduct.metadata?.activeAction && 
            [`ADD_NEW_PRODUCT_LINE`, 'ADD_EXISTING_PRODUCT_LINE', `CHANGE_REQUEST_FOR_PRODUCT_REQUIRING_UPISSUE`].includes(selectedProduct.metadata?.activeAction?.type)
            ) || !selectedProduct.metadata.issueNumber //handle legacy. I.e doesn't have an issue number

          const indexDoc: IndexProductDocument = {
            ...selectedProduct,
            metadata: {
              ...selectedProduct.metadata,
              status: APPROVED,
              lastModerated: {
                byUserId: loggedInUser.user_id,
                byFullName: loggedInUser.name,
                timestamp: moment.utc().format(DATE_TIME_FORMAT)
              },
            }
          }

          const accessToken: string = await getAuth0AccessToken()

          const response = await fetch(
              `${config.apiUrl}/products/${shouldUpissue ? `upissue` : `approve`}`
                  , {
                  method: 'POST',
                  redirect: 'follow',
                  headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Content-Type": 'application/json'
                  },
                  body: JSON.stringify(indexDoc)
              }); 

          if (response.status === 201) {
              toastSuccess(`Success`)
              setIsMarkAsApprovedLoading(false)
              setDidProductUpdate(true)
              setIsConfirmApprovedModalActive(false)

          } else if (response.status === 400) {
            const error: string = await response.text()
            toastError(error)
            setIsMarkAsApprovedLoading(false)
          } else {
              toastError()
              setIsMarkAsApprovedLoading(false)
          }
      }

      const markAsReadyForReview = async () => {

        setIsMarkReadyForReviewLoading(true)

        const timestamp = moment.utc().format(DATE_TIME_FORMAT)

        const indexDoc: IndexProductDocument = {
          ...selectedProduct,
          metadata: {
            ...selectedProduct.metadata,
            status: READY_FOR_APPROVAL,
            lastModerated: {
              byUserId: loggedInUser.user_id,
              byFullName: loggedInUser.name,
              timestamp:timestamp
            }
          }
        }

        const accessToken: string = await getAuth0AccessToken()

        const response = await fetch(
            `${config.apiUrl}/products/edit`
                , {
                method: 'POST',
                redirect: 'follow',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": 'application/json'
                },
                body: JSON.stringify(indexDoc)
            }); 

        if (response.status === 201) {
            toastSuccess(`Success`)
            setIsMarkReadyForReviewLoading(false)
            setDidProductUpdate(true)
            setIsMarkReadyForReviewModalActive(false)

        } else {
            toastError()
            setIsMarkReadyForReviewLoading(false)
        }
    }

      const onClickRestoreProduct = async (e: React.MouseEvent) => {
        e.preventDefault()

        setIsRestoreProductLoading(true)

        const accessToken: string = await getAuth0AccessToken()

        const response = await fetch(
            `${config.apiUrl}/products/get/${selectedProduct.objectID}/restore`
                , {
                method: 'PATCH',
                redirect: 'follow',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": 'application/json'
                }
            }); 

        if (response.status === 200) {
          toastSuccess(`Success`)
          setIsRestoreProductLoading(false)
          setDidProductUpdate(true)
              
        } else {
          toastError()
          setIsRestoreProductLoading(false) 
        }
      }

      const displayEditInfo = (metadata: Metadata, isUpissue: boolean) => {

        if (isUpissue && !isChangeRequestForProductRequiringUpissue(metadata?.activeAction)) {
          return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Upissued by ${metadata.lastEdited.byFullName} ${metadata.lastEdited.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        if (isArchived(metadata) && metadata.lastArchived) {
            return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Archived by ${metadata.lastArchived.byFullName} ${metadata.lastArchived.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        if (isReadyForApproval(metadata) && metadata.lastModerated) {
          return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Last moderated by ${metadata.lastModerated.byFullName} ${metadata.lastModerated.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        if (isEditsRequested(metadata) && isATypeOfChangeRequest(metadata?.activeAction) && metadata.lastModerated) {
          return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Last moderated by ${metadata.lastModerated.byFullName} ${metadata.lastModerated.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        if (isEditsRequested(metadata) && metadata.lastModerated) {
          return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Last moderated by ${metadata.lastModerated.byFullName} ${metadata.lastModerated.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        if (metadata.lastEdited) {
          return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Last updated by ${metadata.lastEdited.byFullName} ${metadata.lastEdited.byFullName === loggedInUser.name ? `(You)` : ``}`}</span>
        }

        return <span><FontAwesomeIcon icon={faCircleInfo} /> {`Created by ${metadata?.created ? metadata?.created.byFullName : 'Unknown'} ${metadata?.created.byFullName === loggedInUser.name ? `(You)` : ``}`} </span>
      }

    const onClickPage = (pageNo: number, isDisabled: boolean) => isDisabled ? null : search(pageNo, searchQuery, productFilters)

      if (!productTypes || !pipeOpenings || !pipeMaterials) return(
        <div className="container is-fullheight">
            <div className="columns mt-6 pt-6">
                <div className="column is-offset-one-quarter is-half" style={{padding: '5rem', borderRadius: 6}}>
                    <PageLoadSpinner />
                </div>
            </div>
        </div>
    )

    const onClickShowVersionHistoryModal = () => {
      getProductVersions(selectedProduct.objectID)
      setIsVersionHistoryModalActive(true)
    }

    return (

    <div className="container is-fullheight">

          <ConfirmActionModal 
            title={`Are you sure you want to approve #${selectedProduct?.objectID}?`}
            subtitle='Once you approve this item, it will be accessible to everyone via search.'
            Icon={<FontAwesomeIcon icon={faCheck} size={`3x`} />}
            confirmBtnText='Approve'
            isModalActive={isConfirmApprovedModalActive}
            isLoading={isMarkAsApprovedLoading}
            onCancel={() => setIsConfirmApprovedModalActive(false)}
            onConfirmAction={markAsApproved} />

          <ConfirmActionModal 
            title={`Are you sure you want to mark #${selectedProduct?.objectID} ready for review?`}
            subtitle='Once you mark this item ready for review, it will be sent for approval and no longer editable.'
            Icon={<FontAwesomeIcon icon={faCheck} size={`3x`} />}
            confirmBtnText='Send for review'
            isModalActive={isMarkReadyForReviewModalActive}
            isLoading={isMarkReadyForReviewLoading}
            onCancel={() => setIsMarkReadyForReviewModalActive(false)}
            onConfirmAction={markAsReadyForReview} />

          <VersionHistoryModal 
            productVersions={productVersions}
            isModalActive={isVersionHistoryModalActive}
            isLoading={isProductVersionsLoading}
            loggedInUser={loggedInUser}
            setIsVersionHistoryModalActive={setIsVersionHistoryModalActive} />

          <RequestEditsFormModal 
            requestEditsFormModal={requestEditsFormModal} 
            product={selectedProduct}
            loggedInUser={loggedInUser} />
            
        <div className="columns mt-6 pt-6">
            <div className="column is-12 has-background-white" style={{padding: '5rem', borderRadius: 6}}>

                <div className="columns">
                    <div className="column">
                        <h1 className="title is-3 mb-6">Manage Products</h1>
                    </div>
                </div>

                {Object.values(productFilters).some(_ => _.appliedFilters.length > 0 || _.hasNoneSelected) &&
                  <div className="column is-narrow pl-0">
                    <button className="button is-text" aria-haspopup="true" aria-controls="dropdown-menu" onClick={onClearFilters}>
                      <span>Clear filters <FontAwesomeIcon className='pl-2' icon={faTimes} /></span>
                    </button>
                  </div>
                }

                <ProductFiltersMenu
                  productSearchResponse={productSearchResponse}
                  productFilters={productFilters}
                  productTypes={productTypes}
                  onCheckProductFilter={onCheckProductFilter}
                  onCheckNoneFilter={onCheckNoneFilter}
                  menuSize={'small'} />

                <div className="tabs is-small">
                  <ul>
                    <li  className={`${activeTab === ActiveTab.PENDING_REVIEW ? `is-active` : ``}`}><Link to={`/manage/products`}>Pending Review <span style={{fontSize: '0.8em'}} className="ml-1 tag is-rounded has-text-weight-bold">{manageProductCounts?.pendingReview}</span> </Link></li>
                    <li  className={`${activeTab === ActiveTab.EDITS_REQUESTED ? `is-active` : ``}`}><Link to={`/manage/products/edits`}>Edits Requested <span style={{fontSize: '0.8em'}} className="ml-1 tag is-rounded has-text-weight-bold">{manageProductCounts?.editsRequested}</span> </Link></li>
                    <li  className={`${activeTab === ActiveTab.ARCHIVED ? `is-active` : ``}`}><Link to={`/manage/products/archived`}>Archived <span style={{fontSize: '0.8em'}} className="ml-1 tag is-rounded has-text-weight-bold">{manageProductCounts?.archived}</span> </Link></li>
                  </ul>
                </div>

                <div className="columns">
                    <div className="column is-3 pt-1" style={{borderRight: 'rgb(228, 228, 228) 1px solid'}}>

                        <div className="field has-addons pb-3">
                          <div className="control has-icons-right is-expanded">
                            <input 
                              className="input is-small" 
                              type="text" 
                              onChange={onChangeSearchQuery} 
                              onKeyPress={event => event.key === 'Enter' && onClickSearch()} 
                              value={searchQuery} 
                              disabled={productSearchResponse?.results?.length === 0 && searchQuery === ''}
                              autoFocus={true} />

                            {searchQuery !== '' &&
                              <span className="icon is-right is-clickable" onClick={onClearSearch}>
                                <FontAwesomeIcon icon={faXmark} />
                              </span>
                            }
                          </div>
                          <div className="control">
                            <button disabled={productSearchResponse?.results?.length === 0 && searchQuery === ''} className={`button is-link is-light is-outlined is-small`} onClick={onClickSearch}>
                              <FontAwesomeIcon icon={faSearch} />
                            </button>
                          </div>
                        </div>

                        {productSearchResponse && productSearchResponse.results.map((result) => (
                            <div className={`columns p-1 is-clickable ${result.objectID === (selectedProduct && selectedProduct.objectID) ? `has-background-light` : `hoverable-item`}`} key={result.objectID}>
                               <div className="column" onClick={() => setSelectedProduct(result)}>
                                <div className="columns mb-2">
                                  <div className="column p-0 pt-1 pr-1">
                                    <StatusTag metadata={result.metadata} className={`is-pulled-right`} />
                                  </div>
                                  <div className="column p-0 pt-1 pr-1 is-narrow is-pulled-right">
                                    <div className="dropdown is-hoverable">
                                    <div className="dropdown-trigger pl-3 pr-1 is-clickable">
                                        <FontAwesomeIcon aria-haspopup="true" aria-controls="dropdown-menu-product-actions" icon={faEllipsisVertical} size={`lg`} />
                                    </div>
                                        <div className="dropdown-menu" id="dropdown-menu-product-actions" role="menu">
                                          <div className="dropdown-content">
                                            <a href="#" className="is-small dropdown-item" onClick={(e: React.MouseEvent) => { e.preventDefault(); onClickShowVersionHistoryModal() } }>
                                              <span><FontAwesomeIcon icon={faHistory} className="pr-2" /> Version History</span>
                                            </a>
                                            
                                            {Permissions.canAdd(loggedInUser) && 
                                              <>
                                                <hr className="navbar-divider"/>
                                                <Link to={`/add/product`} state={{ duplicateState: selectedProduct }} className="is-small dropdown-item">{<span><FontAwesomeIcon className="pr-2" icon={faCopy} />{`Duplicate`}</span>}</Link> 
                                              </> 
                                            }
                                          </div>
                                        </div>
                                      </div>
                                  </div>
                                </div>
                                <div className="columns">
                                  <div className="column is-narrow">
                                    <FontAwesomeIcon icon={faFile} size='2x' />        
                                  </div>
                                  <div className="column" onClick={() => setSelectedProduct(result)}>
                                      <p>
                                      <span className="has-text-weight-semibold title is-6" >
                                          {result.productCode}
                                      </span>
                                      </p>
                                      <p className="subtitle is-7">{displayEditInfo(result.metadata, isATypeOfUpissue(result.metadata?.activeAction))}</p> 
                                  </div>
                                </div>
                            </div>
                            </div>
                        ))}  

                        {productSearchResponse && 
                          <nav className="pagination is-small" role="navigation" aria-label="pagination">
                            <a className={`pagination-previous ${productSearchResponse.noOfResults === 0 || productSearchResponse.page === 0 ? `is-disabled` : ``}`} title="This is the first page" onClick={() => onClickPage(productSearchResponse.page - 1, productSearchResponse.page === 0)}>Previous</a>
                            <a className={`pagination-next ${productSearchResponse.noOfResults === 0 || (productSearchResponse.page === productSearchResponse.pages - 1) ? `is-disabled` : ``}`} onClick={() => onClickPage(productSearchResponse.page + 1, productSearchResponse.page === productSearchResponse.pages - 1)}>Next page</a>
                          </nav>
                        }
                    </div>
                    <div className="column pr-6 pb-6 pl-6 pt-1">
                      {productSearchResponse && productSearchResponse.noOfResults > 0 && selectedProduct
                      ? 
                      <>
                        <Metadata productCode={selectedProduct.productCode} metadata={selectedProduct?.metadata} loggedInUser={loggedInUser} activeTab={activeTab} />

                        {(activeTab === ActiveTab.PENDING_REVIEW && (isPendingReview(selectedProduct.metadata) || isReadyForApproval(selectedProduct.metadata))) &&
                            <div className="columns">
                              <div className="column">
                                  <div className="field is-grouped is-pulled-right">
                                    
                                    {isAddNewProductLine(selectedProduct.metadata?.activeAction) && (selectedProduct.metadata.lastEdited.byUserId !== loggedInUser.user_id && isPendingReview(selectedProduct.metadata))
                                    ?
                                    <>
                                      <div className="control">
                                          <button className={`button is-small is-primary is-light is-outlined ${isMarkReadyForReviewLoading ? `is-loading` : ``}`}onClick={onClickMarkReadyForReview}>{`Ready for Approval`}<FontAwesomeIcon icon={faCheck} className="pl-2" /></button>
                                      </div>
                                      <div className="control">
                                          <button className={`button is-small is-danger is-light is-outlined ${requestEditsFormModal.isLoading ? `is-loading` : ``}`} onClick={() => requestEditsFormModal.setIsModalActive(true)}>{`Request Edits`}<FontAwesomeIcon icon={faXmark} className="pl-2" /></button>
                                      </div>
                                    </>
                                    :
                                    isAddNewProductLine(selectedProduct.metadata?.activeAction) && isReadyForApproval(selectedProduct.metadata) && (selectedProduct.metadata.lastModerated.byUserId !== loggedInUser.user_id) && isSuperAdmin(loggedInUser)
                                    ?
                                    <>
                                      <div className="control">
                                          <button className={`button is-small is-primary is-light is-outlined ${isMarkAsApprovedLoading ? `is-loading` : ``}`}onClick={onClickMarkAsApproved}>{`Approve`}<FontAwesomeIcon icon={faCheck} className="pl-2" /></button>
                                      </div>
                                      <div className="control">
                                          <button className={`button is-small is-danger is-light is-outlined ${requestEditsFormModal.isLoading ? `is-loading` : ``}`} onClick={() => requestEditsFormModal.setIsModalActive(true)}>{`Request Edits`}<FontAwesomeIcon icon={faXmark} className="pl-2" /></button>
                                      </div>
                                    </>
                                    :
                                    (!isAddNewProductLine(selectedProduct.metadata?.activeAction) && selectedProduct.metadata.lastEdited.byUserId !== loggedInUser.user_id) && isAnyAdmin(loggedInUser)
                                    ?
                                    <>
                                      <div className="control">
                                          <button className={`button is-small is-primary is-light is-outlined ${isMarkAsApprovedLoading ? `is-loading` : ``}`}onClick={onClickMarkAsApproved}>{`Approve`}<FontAwesomeIcon icon={faCheck} className="pl-2" /></button>
                                      </div>
                                      <div className="control">
                                          <button className={`button is-small is-danger is-light is-outlined ${requestEditsFormModal.isLoading ? `is-loading` : ``}`} onClick={() => requestEditsFormModal.setIsModalActive(true)}>{`Request Edits`}<FontAwesomeIcon icon={faXmark} className="pl-2" /></button>
                                      </div>
                                    </>
                                    :
                                    <>
                                    <div className="control">
                                      {isUpissueProduct(selectedProduct.metadata?.activeAction) && isPendingReview(selectedProduct.metadata)
                                      ?
                                      <Link to={`/edit/product/${selectedProduct.productCode}`} state={{from: `UPISSUE_REQUESTED`}}>
                                        <button className={`button is-small is-link is-light is-outlined`}>{`Edit`}<FontAwesomeIcon icon={faPen} className="pl-2" /></button>
                                      </Link>                                      
                                      : selectedProduct.metadata.status === PENDING_REVIEW
                                      ?
                                      <Link to={`/edit/product/${selectedProduct.productCode}`} state={{from: `PENDING_REVIEW_EDIT`}}>
                                        <button className={`button is-small is-link is-light is-outlined`}>{`Edit`}<FontAwesomeIcon icon={faPen} className="pl-2" /></button>
                                      </Link>
                                      : selectedProduct.metadata.status !== READY_FOR_APPROVAL
                                      ?
                                      <Link to={`/edit/product/${selectedProduct.productCode}`}>
                                        <button className={`button is-small is-link is-light is-outlined`}>{`Edit`}<FontAwesomeIcon icon={faPen} className="pl-2" /></button>
                                      </Link>
                                      : 
                                      null
                                      }
                                    </div>
                                    </>
                                    }
                                  </div>
                                </div>
                            </div>
                        }

                        {activeTab === ActiveTab.ARCHIVED && selectedProduct.metadata.status === ARCHIVED &&
                            <div className="columns">
                              <div className="column">
                                  <div className="field is-grouped is-pulled-right">
                                    {isAnyAdmin(loggedInUser)
                                    &&
                                    <>
                                      <div className="control">
                                          <button className={`button is-small is-primary is-light is-outlined ${isRestoreProductLoading ? `is-loading` : ``}`}onClick={onClickRestoreProduct}>{`Restore`}<FontAwesomeIcon icon={faTrashRestore} size={`lg`} className="pl-2" /></button>
                                      </div>
                                    </>
                                    }
                                  </div>
                                </div>
                            </div>
                        }

                        <ManageProductView 
                          product={selectedProduct}
                          isMerchant={isMerchant(loggedInUser)} 
                          pipeMaterials={pipeMaterials}
                          pipeOpenings={pipeOpenings}
                          productTypes={productTypes} 
                          flowControlDevices={flowControlDevices} />
                      </>
                      :
                      productSearchResponse && productSearchResponse.noOfResults == 0
                      ?
                      <div className="has-text-centered">
                        {activeTab === ActiveTab.EDITS_REQUESTED
                        ?
                        <p className="title is-5 has-text-centered">{`There are no edits requested.`}</p>
                        :
                        activeTab === ActiveTab.ARCHIVED
                        ?
                        <p className="title is-5 has-text-centered">{`There are no archived products.`}</p>
                        :
                        <p className="title is-5 has-text-centered">{`There are no products for review.`}</p>
                        }
                      </div>
                      :
                      <PageLoadSpinner />
                      }

                    </div>
                </div>


            </div>
        </div>

    </div>
    )

}

export { ManageProductsScreen }